import { Link } from "react-router";

import { openStandaloneWindow } from "../../utils/window";

import type { MouseEvent, ReactNode } from "react";
import type { LinkProps, To } from "react-router";
import type { WindowId } from "../../constants/tee";

export interface TruLinkProps extends LinkProps {
  hasChangesCallback?: (to: To, clickHandler?: () => void) => void;
  clickHandler?: () => void;
  preventNavigation?: boolean;
  singleInstanceId?: WindowId;
  currentUrl?: string;
  isActive?: boolean;
  isErrorMode?: boolean;
  encounterOpened?: boolean;
  openInNewTab?: boolean;
  testId?: string;
  children?: ReactNode;
}

export const TruLink = ({
  children,
  className = "",
  clickHandler,
  currentUrl,
  encounterOpened = false,
  hasChangesCallback,
  id,
  isActive,
  isErrorMode = false,
  openInNewTab = false,
  preventNavigation = false,
  replace,
  singleInstanceId,
  tabIndex,
  testId,
  to,
}: TruLinkProps) => {
  const linkClassName = isActive || to === currentUrl ? `${className} active` : className;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (singleInstanceId) {
      event.preventDefault();
      openStandaloneWindow(singleInstanceId, to as string);
      return;
    }

    if (preventNavigation) {
      event.preventDefault();
      return;
    }

    if (hasChangesCallback && !encounterOpened) {
      event.preventDefault();
      hasChangesCallback(to, clickHandler);
    } else if (clickHandler) {
      // After this handler returns, react-router Link performs navigation.
      queueMicrotask(clickHandler);
    } else if (encounterOpened || openInNewTab) {
      event.preventDefault();
      window.open(`${process.env.PUBLIC_URL}${to}`);
    }
  };

  if (isErrorMode) {
    return (
      <a
        className={linkClassName}
        data-testid={testId}
        href={`${process.env.PUBLIC_URL}${to.toString()}`}
        id={id}
        onClick={handleClick}
        rel="noreferrer"
        tabIndex={tabIndex}
        target={openInNewTab ? "_blank" : "_self"}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      className={linkClassName}
      data-testid={testId}
      id={id}
      onClick={handleClick}
      replace={replace}
      tabIndex={tabIndex}
      target={encounterOpened || openInNewTab ? "_blank" : "_self"}
      to={to}
    >
      {children}
    </Link>
  );
};

export default TruLink;
